import type React from "react";
import { i18n } from "@frontend/i18n";
import { Button } from "@planta-v2/pds-ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@planta-v2/pds-ui/dialog";
import type { DirectoryExportModalState } from "../controllers/useDirectoryExportSectionController";

export interface DirectoryExportModalProps {
  readonly state: DirectoryExportModalState;

  readonly onClose: () => void;

  readonly onConfirm: () => void;
}

interface ModalCopy {
  readonly title: string;
  readonly body: string;
}

/**
 * The file lands in a different app, so the outcome has to be said here: a
 * member who taps Send and sees the modal close has no way to tell a delivery
 * from a closed window.
 */
function resolveCopy(state: DirectoryExportModalState): ModalCopy {
  switch (state) {
    case "queued":
      return {
        title: i18n.t("user.profile.preferences.export.modal.queuedTitle"),
        body: i18n.t("user.profile.preferences.export.modal.queuedBody"),
      };
    case "needsInboundMessage":
      return {
        title: i18n.t(
          "user.profile.preferences.export.modal.needsInboundTitle",
        ),
        body: i18n.t("user.profile.preferences.export.modal.needsInboundBody"),
      };
    case "error":
      return {
        title: i18n.t("user.profile.preferences.export.modal.title"),
        body: i18n.t("user.profile.preferences.export.modal.errorBody"),
      };
    case "closed":
    case "confirm":
    case "sending":
      return {
        title: i18n.t("user.profile.preferences.export.modal.title"),
        body: i18n.t("user.profile.preferences.export.modal.body"),
      };
  }
}

export function DirectoryExportModal({
  state,
  onClose,
  onConfirm,
}: DirectoryExportModalProps): React.JSX.Element {
  const isAwaitingConfirmation = state === "confirm" || state === "error";
  const isSending = state === "sending";
  const { title, body } = resolveCopy(state);

  return (
    <Dialog
      open={state !== "closed"}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        data-testid="directory-export-modal"
        className="flex w-[357px] flex-col gap-3 p-4 sm:max-w-none"
        showCloseButton={false}
      >
        <DialogTitle className="text-xl font-medium">{title}</DialogTitle>
        <DialogDescription
          data-testid="directory-export-modal-body"
          className="text-sm text-muted-foreground"
        >
          {body}
        </DialogDescription>
        <div className="flex justify-end gap-2">
          {isAwaitingConfirmation || isSending ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                disabled={isSending}
                data-testid="directory-export-modal-cancel"
              >
                {i18n.t("user.profile.preferences.export.modal.cancel")}
              </Button>
              <Button
                size="sm"
                onClick={onConfirm}
                disabled={isSending}
                data-testid="directory-export-modal-confirm"
              >
                {isSending
                  ? i18n.t("user.profile.preferences.export.modal.sending")
                  : i18n.t("user.profile.preferences.export.modal.confirm")}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              onClick={onClose}
              data-testid="directory-export-modal-close"
            >
              {i18n.t("user.profile.preferences.export.modal.close")}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
